
import React, { createContext, useContext, useState, useEffect } from 'react';
import { SlimeData, SlimeMood, SlimeStage } from '@/types/slimeHabit';
import { toast } from 'sonner';

// 定义默认史莱姆状态
const defaultSlimeData: SlimeData = {
  stage: 'spark',
  mood: 'neutral',
  customizations: {},
  stats: {
    experience: 0,
    level: 1,
    happiness: 50,
    energy: 100
  },
  unlockedForms: [],
  lastFeedTime: null
};

// 创建上下文
interface SlimeContextType {
  slimeData: SlimeData;
  updateMood: (mood: SlimeMood) => void;
  updateStage: (stage: SlimeStage) => void;
  addExperience: (amount: number) => void;
  updateEnergy: (amount: number) => void;
  updateHappiness: (amount: number) => void;
  unlockForm: (formId: string) => void;
  resetSlimeData: () => void;
  feedSlime: (content: string) => { success: boolean; rewards: { type: string; amount: number }[] };
  canFeedSlime: () => boolean;
}

const SlimeContext = createContext<SlimeContextType | undefined>(undefined);

// 创建提供者组件
export const SlimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [slimeData, setSlimeData] = useState<SlimeData>(() => {
    // 从本地存储加载数据（如果有）
    const savedData = localStorage.getItem('slimeData');
    return savedData ? JSON.parse(savedData) : defaultSlimeData;
  });

  // 当史莱姆数据变化时保存到本地存储
  useEffect(() => {
    localStorage.setItem('slimeData', JSON.stringify(slimeData));
  }, [slimeData]);

  // 更新心情
  const updateMood = (mood: SlimeMood) => {
    setSlimeData(prev => ({ ...prev, mood }));
  };

  // 更新成长阶段
  const updateStage = (stage: SlimeStage) => {
    setSlimeData(prev => ({ ...prev, stage }));
  };

  // 添加经验值
  const addExperience = (amount: number) => {
    setSlimeData(prev => {
      const newExp = prev.stats.experience + amount;
      // 简单的升级逻辑：每100点经验升1级
      const newLevel = Math.floor(newExp / 100) + 1;
      
      return {
        ...prev,
        stats: {
          ...prev.stats,
          experience: newExp,
          level: newLevel
        }
      };
    });
  };

  // 更新能量
  const updateEnergy = (amount: number) => {
    setSlimeData(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        energy: Math.max(0, Math.min(100, prev.stats.energy + amount))
      }
    }));
  };

  // 更新快乐度
  const updateHappiness = (amount: number) => {
    setSlimeData(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        happiness: Math.max(0, Math.min(100, prev.stats.happiness + amount))
      }
    }));
  };

  // 解锁新形态
  const unlockForm = (formId: string) => {
    setSlimeData(prev => ({
      ...prev,
      unlockedForms: [...prev.unlockedForms, formId]
    }));
  };

  // 重置史莱姆数据
  const resetSlimeData = () => {
    setSlimeData(defaultSlimeData);
  };

  // 检查是否可以投喂史莱姆（每天只能投喂一次）
  const canFeedSlime = () => {
    if (!slimeData.lastFeedTime) return true;
    
    const lastFeed = new Date(slimeData.lastFeedTime);
    const now = new Date();
    
    // 检查是否是同一天
    return lastFeed.getDate() !== now.getDate() || 
           lastFeed.getMonth() !== now.getMonth() || 
           lastFeed.getFullYear() !== now.getFullYear();
  };

  // 投喂史莱姆（通过吐槽）
  const feedSlime = (content: string) => {
    // 检查是否可以投喂
    if (!canFeedSlime()) {
      return { success: false, rewards: [] };
    }
    
    // 根据吐槽内容长度计算奖励
    const contentLength = content.trim().length;
    let expReward = 0;
    let energyReward = 0;
    let happinessReward = 0;
    
    // 基础奖励
    if (contentLength > 0) {
      // 基础奖励 + 根据长度增加奖励
      expReward = Math.min(10 + Math.floor(contentLength / 10), 30);
      energyReward = Math.min(5 + Math.floor(contentLength / 20), 15);
      happinessReward = Math.min(5 + Math.floor(contentLength / 15), 20);
    }
    
    // 更新史莱姆状态
    setSlimeData(prev => ({
      ...prev,
      lastFeedTime: new Date().toISOString(),
      stats: {
        ...prev.stats,
        experience: prev.stats.experience + expReward,
        energy: Math.min(100, prev.stats.energy + energyReward),
        happiness: Math.min(100, prev.stats.happiness + happinessReward)
      }
    }));
    
    // 如果吐槽内容很长，给予额外的情绪变化
    if (contentLength > 50) {
      updateMood('joyful');
    }
    
    // 返回获得的奖励
    return {
      success: true,
      rewards: [
        { type: 'experience', amount: expReward },
        { type: 'energy', amount: energyReward },
        { type: 'happiness', amount: happinessReward }
      ]
    };
  };

  return (
    <SlimeContext.Provider value={{
      slimeData,
      updateMood,
      updateStage,
      addExperience,
      updateEnergy,
      updateHappiness,
      unlockForm,
      resetSlimeData,
      feedSlime,
      canFeedSlime
    }}>
      {children}
    </SlimeContext.Provider>
  );
};

// 自定义钩子
export const useSlime = () => {
  const context = useContext(SlimeContext);
  if (context === undefined) {
    throw new Error('useSlime must be used within a SlimeProvider');
  }
  return context;
};
